import useRetailerApi from '@/useRetailerApi'
import useTranslation from '@/hooks/useTranslation'
import useCart from '@/hooks/useCart'
import { LANGUAGE_LOCALES } from '@/locales/languages'
import Button from '@/components/Button'
import OptimizedImage from '@/components/OptimizedImage'

const STOCK_CONFIG = {
    available: {
        color: 'text-green-500',
        dot: 'bg-green-500',
        key: 'dashboard.inStock',
    },
    low: {
        color: 'text-orange-400',
        dot: 'bg-orange-400',
        key: 'dashboard.stockLow',
    },
    unavailable: {
        color: 'text-red-400',
        dot: 'bg-red-400',
        key: 'dashboard.outOfStock',
    },
}

function ProductCard({ product, onClick }) {
    const { retailer } = useRetailerApi()
    const { t, language } = useTranslation()
    const { getProductQuantity } = useCart()
    const isAdmin = retailer.access_level === 'admin'
    const cartQty = getProductQuantity(product.title)
    const discount = 1 - (retailer.default_markup_rate ?? 0)

    const minPrice = Math.min(
        ...product.variants.map((variant) => variant.price * discount)
    )
    const maxPrice = Math.max(
        ...product.variants.map((variant) => variant.price * discount)
    )
    const stockPriority = { available: 2, low: 1, unavailable: 0 }
    const bestStatus = product.variants.reduce(
        (best, v) =>
            stockPriority[v.stock_status] > stockPriority[best]
                ? v.stock_status
                : best,
        'unavailable'
    )

    return (
        <div
            className="flex flex-col gap-[8px] cursor-pointer"
            onClick={onClick}
        >
            <div className="aspect-442/589 bg-background-product shrink-0 px-[40px] sm:px-[60px] md:px-[80px] flex items-center justify-center relative rounded-[8px] group">
                <div className="p-[24px] font-semibold leading-none text-[14px] absolute top-0 left-0 right-0">
                    <div className="h-[34px] flex justify-between items-center w-full">
                        <StockBadge status={bestStatus} />

                        {cartQty > 0 && (
                            <div className="bg-white rounded-[4px] w-[34px] h-[34px] flex items-center justify-center ml-auto">
                                {cartQty}
                            </div>
                        )}
                    </div>
                </div>

                <OptimizedImage
                    className="object-cover "
                    src={product.image_url}
                    alt={product.title}
                    sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw"
                    widths={[320, 480, 640, 800]}
                />

                <div className="absolute bottom-fluid-20 left-fluid-20 right-fluid-20 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant="light" fullWidth>
                        {t('dashboard.configure')}
                    </Button>
                </div>
            </div>

            <div className="flex flex-col gap-[8px]">
                <span className="text-[14px] uppercase">{product.title}</span>
                {isAdmin && (
                    <span className="text-[14px]">
                        {(minPrice / 100).toLocaleString(
                            LANGUAGE_LOCALES[language],
                            {
                                style: 'currency',
                                currency: 'EUR',
                            }
                        )}
                        {minPrice !== maxPrice &&
                            ' – ' +
                                (maxPrice / 100).toLocaleString(
                                    LANGUAGE_LOCALES[language],
                                    {
                                        style: 'currency',
                                        currency: 'EUR',
                                    }
                                )}
                    </span>
                )}
            </div>
        </div>
    )
}

function StockBadge({ status }) {
    const { t } = useTranslation()
    const config = STOCK_CONFIG[status]
    if (!config) return null

    return (
        <div className={`${config.color} flex items-center gap-[8px]`}>
            <div
                className={`h-[6px] w-[6px] shrink-0 ${config.dot} rounded-full`}
            />
            <span className="uppercase text-[14px] -mt-px leading-none">
                {t(config.key)}
            </span>
        </div>
    )
}

export default ProductCard
